import './styles.scss';
import moment from 'moment';
import { useContext, useEffect, useMemo, useRef } from 'react';

import { BitcoinIcon } from '../../../../../components/bitcoin-icon';
import { CalendarInteractive } from '../../../../../components/calendar-interactive/calendar-interactive';
import { ReportFilterButtons } from '../../../../../components/report-filter-buttons';
import { ReportFilterMultiSelect } from '../../../../../components/report-filter-buttons/report-filter-multi-select';
import Select from '../../../../../components/select/select';
import { PORTFOLIO_TABS_NAME } from '../../../../../constants/reports/reports-data';
import { PORTFOLIO_CONTRACTS } from '../../../../../constants/reports/reports-filters';
import { GlobalContext } from '../../../../../providers/global-provider';
import {
  DATA_DISPLAY_KEYS,
  PortfolioManagementContext,
} from '../../../../../providers/portfolio-management';
import {
  TransactionsFiltersContext,
  VENUE_TYPE_LIST_TRANSACTIONS,
} from '../../../../../providers/transactions/transactions-filters-provider';
import { TransactionsContext } from '../../../../../providers/transactions/transactions-provider';
import { IAccountOption } from '../../../../../providers/venue-provider';
import Utils from '../../../../../utils/immersive_utils';

import TransactionTable from './transactions-table';

interface TransactionsBodyProps {
  accountOption: IAccountOption;
}

type TypeDataType = {
  value: string;
  label: string;
  isVisible: boolean;
};

type TransactionItemType = {
  instrument_name: string;
  currency: string;
  timestamp: string;
  type: string;
  side: string;
  amount: string;
  session_rpl: string;
  session_upl: string;
  position: string;
  price: string;
  mark_price: string;
  change: string;
  balance: string;
  trade_id: string;
  order_id: string;
  info: string;
  equity?: string;
  markPrice?: string;
  values: any[];
};

const values = [
  {
    value: 'timestamp',
    type: 'string',
    formatter: (value: string) => {
      return moment(value).format('DD MMM YYYY, HH:mm:ss');
    },
  },
  {
    value: 'event_type',
    type: 'string',
  },
  {
    value: 'side',
    type: 'string',
    formatter: (val: any) => {
      const GREEN = ['long', 'close buy', 'buy', 'open buy'];
      const RED = ['short', 'open sell', 'sell', 'close sell'];

      if (GREEN.includes(val.toLowerCase())) {
        return <span className="formatter-green">{val.toLowerCase()}</span>;
      }

      if (RED.includes(val.toLowerCase())) {
        return <span className="formatter-red">{val.toLowerCase()}</span>;
      }

      return <span>{val}</span>;
    },
  },
  {
    value: 'amount',
    type: 'number',
    formatter: (val: any, element: any) => {
      const isNegative = parseFloat(val) < 0;

      if (isNegative) {
        return (
          <div className="formatter-negative">
            {element['price_currency'] === 'USD'
              ? `$ (${Utils.formatNumber(val * -1, 1)})`
              : `(${Utils.formatNumber(val * -1, 1)})`}
          </div>
        );
      } else {
        return element['price_currency'] === 'USD'
          ? `$ ${Utils.formatNumber(val, 1)}`
          : Utils.formatNumber(val, 1);
      }
    },
  },
  {
    value: 'position',
    type: 'number',
    formatter: (val: any, element: any) => {
      const isNegative = parseFloat(val) < 0;

      if (isNegative) {
        return (
          <div className="formatter-negative">
            {element['price_currency'] === 'USD'
              ? `$ (${Utils.formatNumber(val * -1, 1)})`
              : `(${Utils.formatNumber(val * -1, 1)})`}
          </div>
        );
      } else {
        return element['price_currency'] === 'USD'
          ? `$ ${Utils.formatNumber(val, 1)}`
          : Utils.formatNumber(val, 1);
      }
    },
  },
  {
    value: 'price',
    type: 'number',
    formatter: (val: any, element: any) => {
      if (element['price_currency'] === 'BTC') {
        return (
          <div>
            <BitcoinIcon />
            {Utils.formatNumber(val, 4)}
          </div>
        );
      }
      if (element['price_currency'] === 'USD') {
        return `$ ${Utils.formatNumber(val, 4)}`;
      }

      return `${Utils.formatNumber(val, 4)}`;
    },
  },
  {
    value: 'mark_price',
    type: 'number',
    formatter: (val: any, element: any) => {
      if (element['price_currency'] === 'BTC') {
        return (
          <div>
            <BitcoinIcon />
            {Utils.formatNumber(val, 6)}
          </div>
        );
      }
      if (element['price_currency'] === 'USD') {
        return `$ ${Utils.formatNumber(val, 6)}`;
      }

      return `${Utils.formatNumber(val, 6)}`;
    },
  },
  {
    value: 'cashflow',
    type: 'cashflow',
  },
  {
    value: 'change',
    type: 'number',
    formatter: (val: any) => {
      if (parseFloat(val) < 0)
        return <div className="formatter-negative">({Utils.formatNumber(val * -1, 6)})</div>;

      return Utils.formatNumber(val, 6);
    },
  },
  {
    value: 'balance',
    type: 'number',
    formatter: (val: any) => {
      return Utils.formatNumber(val, 6);
    },
  },
  {
    value: 'equity',
    type: 'number',
    formatter: (val: any) => {
      return Utils.formatNumber(val, 8);
    },
  },
  { value: 'trade_id', type: 'string' },
  { value: 'order_id', type: 'string' },
  { value: 'info', type: 'string' },
];

const HEADER = [
  'Date',
  'Type',
  'Side',
  'Amount',
  'Position',
  'Price',
  'MarkPrice',
  'CashFlow',
  'Change',
  'Balance',
  'Equity',
  'Trade ID',
  'Order ID',
  'Info',
];

const mappingToOKX = (okxData: any) => {
  return {
    instrument_name: okxData.instId,
    instrument_type: okxData.instrument_type,
    currency: okxData.ccy,
    timestamp: okxData.ts,
    event_type: okxData.event_type,
    side: okxData.side,
    amount: okxData.sz,
    session_rpl: okxData.session_rpl,
    session_upl: okxData.session_upl,
    position: okxData.posBalChg,
    price: okxData.px,
    mark_price: okxData.fillMarkPx,
    change: okxData.balChg,
    balance: okxData.bal,
    trade_id: okxData.tradeId,
    order_id: okxData.ordId,
    info: okxData.notes,
    values: okxData.values,
  };
};

const mappingToOTC = (otcData: any) => {
  return {
    instrument_name: otcData.instrument_name,
    instrument_type: otcData.instrument_type,
    currency: otcData.symbol,
    timestamp: otcData.timestamp,
    event_type: otcData.type,
    side: otcData.side,
    amount: otcData.quantity,
    session_rpl: otcData.session_rpl,
    session_upl: otcData.session_upl,
    position: otcData.position,
    price: otcData.price,
    mark_price: otcData.mark_price,
    change: otcData.change,
    balance: otcData.balance,
    trade_id: otcData.transaction_id,
    order_id: otcData.order_id,
    info: otcData.info,
    values: otcData.values,
  };
};

const mappingToBullish = (bullishData: any) => {
  return bullishData;
};

const mappingToCoincall = (coincallData: any) => {
  return coincallData;
};

function groupByHierarchy(
  data: any[],
  allowedEventTypes: string[],
  allowedInstTypes: string[],
  searchString: string,
  token: string,
) {
  const groupedData: Record<string, any> = {};

  const processValues = (values: any[]) => values.map((_, index) => <div key={index}>&nbsp;</div>);

  data?.forEach((item) => {
    const { exchange, timestamp, event_type, instrument_type } = item;

    const date = timestamp?.split(' ')[0];

    const isAllowedEventType = allowedEventTypes.includes(event_type);
    const isAllowedInstType = allowedInstTypes.includes(instrument_type);
    const itemCurrency = item.currency || item.ccy || item.symbol || '';

    const isTokenMatch = itemCurrency?.toLowerCase() === token?.toLowerCase();

    const isMatch =
      searchString.length === 0 ||
      !!Object.values(item).filter((param: any) => {
        const paramString = typeof param === 'string' ? param : param.toString();

        return (
          !!paramString.toLowerCase().includes(searchString?.toLowerCase()) &&
          paramString?.length > 0
        );
      })?.length;

    if (isAllowedEventType && isAllowedInstType && isTokenMatch && isMatch) {
      if (!groupedData[exchange]) {
        groupedData[exchange] = {
          id: exchange,
          label: exchange,
          children: [],
          values: processValues(item.values),
        };
      }

      const exchangeNode = groupedData[exchange];

      let dateNode = exchangeNode.children.find((child: any) => child.id === `${exchange}_${date}`);
      if (!dateNode) {
        dateNode = {
          id: `${exchange}_${date}`,
          label: date,
          children: [],
          values: processValues(item.values),
          timestamp: item.timestamp,
        };
        exchangeNode.children.push(dateNode);
      }

      dateNode.children.push({
        ...item,
        values: values.map((val: any, index: any) => {
          if (item[val.value] === undefined) {
            return '';
          }
          if (val.formatter) {
            return val.formatter(item[val.value], item);
          } else {
            if (val.type === 'cashflow') {
              return (
                <div
                  key={index}
                  className={`transaction-cashflow ${item[val.value] >= 0 ? 'formatter-green' : 'formatter-red'}`}
                >
                  <span>{Utils.formatNumber(item[val.value], 6)}</span>
                  {item['session_upl']?.length ? <span>(UPL: {item['session_upl']})</span> : null}
                  {item['session_rpl']?.length ? <span>(RPL: {item['session_rpl']})</span> : null}
                </div>
              );
            }

            return item[val.value];
          }
        }),
      });

      exchangeNode.children.sort((a: any, b: any) => {
        const dateA = new Date(a.timestamp) || new Date();
        const dateB = new Date(b.timestamp) || new Date();

        return dateB.getTime() - dateA.getTime();
      });
    }
  });

  const newData = Object.values(groupedData);

  return newData;
}

const TransactionBody = ({ accountOption }: TransactionsBodyProps) => {
  const { portfolioCurrentTab } = useContext(GlobalContext);
  const {
    filters: {
      transactionsEventType,
      setTransactionsEventType,
      transactionsInstType,
      setTransactionsInstType,
      transactionsVenue,
      setTransactionsVenue,
      token,
      tokenList,
      wasCalculated,
      setToken,
      setTokenList,
      setWasCalculated,
      setErrorTransactions,
      eventTypeList,
      setEventTypeList,
      instTypeList,
      setInstTypeList,
    },
  } = useContext(TransactionsFiltersContext);
  const search = '';

  const {
    data,
    setData,
    isLoadingTransactions,
    lastUpdateTransactions,
    setLastUpdateTransactions,
    getData,
  } = useContext(TransactionsContext);
  const { getDataFromAccount, selectedDataDisplayOption, getAggregatedData, aggregatedData } =
    useContext(PortfolioManagementContext);

  const isDataDisplayPerAccount = selectedDataDisplayOption === DATA_DISPLAY_KEYS.perAccount;
  const isFirstLoadFiltersTransactionsTypes = useRef<boolean>(true);

  const { TRANSACTIONS } = PORTFOLIO_CONTRACTS;

  const groupedData = useMemo(() => {
    if (portfolioCurrentTab !== PORTFOLIO_TABS_NAME.transactions) return [];

    let newData: any[] = [];

    const sanitalizedData: TransactionItemType[] = [];

    const instTypeListArr: TypeDataType[] = [];
    const eventTypeListArr: TypeDataType[] = [];

    if (!data || data?.length === 0) {
      setEventTypeList(eventTypeListArr);
      setInstTypeList(instTypeListArr);

      return [];
    }

    const uniqueTokens: string[] = Array.from(
      new Set(data.map((item: any) => item.currency || item.ccy || item.symbol)),
    );

    const handleTokenList = (tokens: string[]) => {
      const getLabel = (token: string) => {
        let label = token;

        const spplitTokenLabel = (token: string, suffix: string) => {
          if (!token) return null;
          if (token.includes(suffix)) {
            return `${token.replace(suffix, '')} ${suffix}`;
          }

          return null;
        };

        const suffixs = ['USDC', 'USDT', 'USD'];

        suffixs.some((suffix) => {
          const nLabel = spplitTokenLabel(label, suffix);
          if (nLabel) {
            label = nLabel;
            return true;
          }
          return false;
        });

        return label;
      };

      const sortedTokens = Utils.naturalSort(tokens);

      const newTokenList = sortedTokens.map((tk) => ({
        value: tk,
        label: getLabel(tk),
        isVisible: true,
      }));

      const hasDataForToken = data.find(
        (item: any) => (item.currency || item.ccy || item.symbol) === token,
      );

      if (sortedTokens && sortedTokens.length && !hasDataForToken) {
        setToken(sortedTokens[0]);
      }

      setTokenList(newTokenList);
    };

    handleTokenList(uniqueTokens);

    setWasCalculated(true);

    data?.forEach((elementRaw: any, index: number) => {
      const element =
        accountOption.exchange === 'okx'
          ? mappingToOKX(elementRaw)
          : accountOption.exchange === 'otc'
            ? mappingToOTC(elementRaw)
            : accountOption.exchange === 'bullish'
              ? mappingToBullish(elementRaw)
              : accountOption.exchange === 'coincall'
                ? mappingToCoincall(elementRaw)
                : elementRaw;

      let timestamp = element.timestamp;

      const hasEventTypeOnList = eventTypeListArr.some(
        (eventTypeListItem) => eventTypeListItem.value === element?.event_type,
      );

      const hasInstTypeOnList = instTypeListArr.some(
        (instTypeListItem) => instTypeListItem.value === element?.instrument_type,
      );

      const label = Utils.capitalizeFirstLetter(
        (element?.event_type || '').replace('_', ' ').toLowerCase(),
      );

      if (!hasEventTypeOnList) {
        eventTypeListArr.push({
          value: element.event_type,
          label: label,
          isVisible: true,
        });

        if (
          !transactionsInstType.includes(element.event_type) &&
          isFirstLoadFiltersTransactionsTypes.current
        ) {
          setTransactionsEventType((state) => [...state, element.event_type]);
        }
      }

      if (!hasInstTypeOnList) {
        instTypeListArr.push({
          value: element.instrument_type,
          label: Utils.capitalizeFirstLetter(element?.instrument_type) ?? '',
          isVisible: true,
        });

        if (
          !transactionsInstType.includes(element.instrument_type) &&
          isFirstLoadFiltersTransactionsTypes.current
        ) {
          setTransactionsInstType((state) => [...state, element.instrument_type]);
        }
      }

      if (accountOption.exchange === 'okx') {
        const date = moment.utc(parseInt(timestamp));

        timestamp = date.format('YYYY-MM-DD HH:mm:ss.SSS');
      }

      const exchangeMaxLengthForUpperCase = 3;

      sanitalizedData[index] = {
        ...element,
        timestamp,
        exchange:
          accountOption?.exchange.length <= exchangeMaxLengthForUpperCase
            ? accountOption?.exchange.toLocaleUpperCase()
            : Utils.capitalizeFirstLetter(accountOption?.exchange),
        label: element['instrument_name'] || element['event_type'] || '-',
        values: values.map((val: any) => {
          if (element[val.value] === undefined) {
            return '';
          }

          if (val.type === 'number') {
            return parseFloat(element[val.value] || '0');
          } else {
            return element[val.value] || '-';
          }
        }),
      };
    });

    if (transactionsVenue === 'deribit' && isFirstLoadFiltersTransactionsTypes.current) {
      setTransactionsInstType(TRANSACTIONS.CONTRACT_VALUES);
    }

    isFirstLoadFiltersTransactionsTypes.current = false;
    setEventTypeList(eventTypeListArr);
    setInstTypeList(instTypeListArr);

    const instTypeValuesArr: any =
      transactionsVenue === 'deribit'
        ? TRANSACTIONS.CONTRACT_VALUES
        : instTypeListArr.map((item) => item.value);

    const allowedEventTypes = eventTypeListArr
      .map((eventType) => eventType.value)
      .filter((eventType) => transactionsEventType.includes(eventType));

    const allowedInstTypes = instTypeValuesArr.filter((instType: any) =>
      transactionsInstType.includes(instType),
    );

    newData = groupByHierarchy(sanitalizedData, allowedEventTypes, allowedInstTypes, search, token);

    return [newData];
  }, [
    data,
    search,
    transactionsEventType,
    transactionsInstType,
    token,
    transactionsVenue,
    setTokenList,
    accountOption,
    setEventTypeList,
  ]);

  useEffect(() => {
    if (
      isLoadingTransactions ||
      !isDataDisplayPerAccount ||
      portfolioCurrentTab !== PORTFOLIO_TABS_NAME.transactions
    )
      return;

    const dataAccount = getDataFromAccount('TRANSACTIONS', accountOption);
    const exchange = accountOption?.exchange;
    const defineVenue = ['deribit', 'okx', 'otc'].includes(exchange) ? exchange : '';

    if (!dataAccount) {
      setData(null);
      setErrorTransactions(false);
      getData(accountOption);
      return;
    }

    if (dataAccount.error) {
      setData(null);
      setErrorTransactions(true);
      return;
    }

    if (dataAccount.data) {
      setData(dataAccount.data);
      setLastUpdateTransactions(dataAccount.lastUpdate);
      setTransactionsVenue(defineVenue);
      setErrorTransactions(false);
      return;
    }

    setData(null);
    setErrorTransactions(false);
  }, [accountOption, isLoadingTransactions, portfolioCurrentTab, isDataDisplayPerAccount]);

  useEffect(() => {
    if (
      isLoadingTransactions ||
      isDataDisplayPerAccount ||
      portfolioCurrentTab !== PORTFOLIO_TABS_NAME.transactions
    )
      return;

    const dataAggregatedAccounts = getAggregatedData('TRANSACTIONS');

    const AggregatedAccountsFilteredByExchangeDeribit = aggregatedData.accounts.filter(
      (account) => account.exchange === 'deribit',
    );

    if (!dataAggregatedAccounts) {
      setData(null);
      setErrorTransactions(false);

      if (AggregatedAccountsFilteredByExchangeDeribit.length > 0) {
        getData(AggregatedAccountsFilteredByExchangeDeribit, aggregatedData.accounts);
      }
      return;
    }

    if (
      (transactionsVenue === 'deribit' && dataAggregatedAccounts.error.deribit) ||
      (transactionsVenue === 'okx' && dataAggregatedAccounts.error.okx)
    ) {
      setData(null);
      setErrorTransactions(true);
      return;
    }

    if (dataAggregatedAccounts.data) {
      setData(dataAggregatedAccounts.data[transactionsVenue]);
      setLastUpdateTransactions(dataAggregatedAccounts.lastUpdate);
      setErrorTransactions(false);
      return;
    }

    setData(null);
    setErrorTransactions(false);
  }, [isLoadingTransactions, portfolioCurrentTab, isDataDisplayPerAccount, transactionsVenue]);

  const renderTable = useMemo(() => {
    const hasOnFilterData = groupedData[0]?.length > 0;
    return (
      <TransactionTable
        data={groupedData}
        header={HEADER}
        lastUpdateTransactions={lastUpdateTransactions}
        hasOnFilterData={hasOnFilterData}
        accountOption={accountOption}
      />
    );
  }, [data, groupedData, isLoadingTransactions, token, tokenList, transactionsVenue]);

  const renderTokenSelect = useMemo(() => {
    const visibleTokenList = tokenList.filter((tk: any) => tk.isVisible);

    const selectedToken =
      visibleTokenList.find((tk: any) => tk.value === token) || visibleTokenList[0];

    const tokenListMapp = visibleTokenList.map((tk: any) => ({
      value: tk.value,
      name: tk.label,
    }));

    return (
      <Select
        key={selectedToken.value}
        defaultValue={{ value: selectedToken.value, name: selectedToken.label }}
        values={tokenListMapp}
        onChange={(val: string) => {
          setToken(val);
        }}
        className="token-select"
        label="Token"
      />
    );
  }, [tokenList, token, setToken]);

  const handleOnApplyCalendar = (beginDate: any, endDate: any) => {
    if (!beginDate || !endDate) return;

    if (isDataDisplayPerAccount) {
      getData(accountOption, [], beginDate, endDate);
      return;
    }

    const AggregatedAccountsFilteredByExchangeDeribit = aggregatedData.accounts.filter(
      (account) => account.exchange === 'deribit',
    );

    if (AggregatedAccountsFilteredByExchangeDeribit.length > 0) {
      getData(
        AggregatedAccountsFilteredByExchangeDeribit,
        aggregatedData.accounts,
        beginDate,
        endDate,
      );
    }
  };

  const renderSearchRow = useMemo(() => {
    const instTypeDataArr =
      transactionsVenue === 'deribit' ? TRANSACTIONS.CONTRACT_KEYS : instTypeList;

    return (
      <div className="search-row">
        <div className="search-row-filters">
          <div className="calendar-filter">
            <CalendarInteractive onApply={handleOnApplyCalendar} isSelectPeriod={false} />
          </div>

          {renderTokenSelect}

          {!isDataDisplayPerAccount && (
            <ReportFilterButtons
              titleFilter="Venue"
              dataArr={VENUE_TYPE_LIST_TRANSACTIONS}
              selected={transactionsVenue}
              setSelected={setTransactionsVenue}
            />
          )}

          {!isLoadingTransactions && (
            <>
              <ReportFilterMultiSelect
                titleFilter="Inst Type"
                dataArr={instTypeDataArr}
                selected={transactionsInstType}
                setSelected={setTransactionsInstType}
              />
              <ReportFilterMultiSelect
                titleFilter="Event Type"
                dataArr={eventTypeList}
                selected={transactionsEventType}
                setSelected={setTransactionsEventType}
              />
            </>
          )}
        </div>
      </div>
    );
  }, [
    search,
    wasCalculated,
    transactionsEventType,
    transactionsInstType,
    transactionsVenue,
    eventTypeList,
    instTypeList,
    isLoadingTransactions,
  ]);

  return (
    <div className="positions-body-container">
      {renderSearchRow}

      <div className="container">{renderTable}</div>
    </div>
  );
};

export default TransactionBody;
